import React, { useEffect, useRef, useState } from 'react'

import { COLOR } from '@ui-kit/constants'

import { ANIMATION_DURATION, HALF_DIVISOR, ROTATION_DEGREES } from './constants'
import { circularProgress } from './styles'
import type { Props } from './types'
import calculateProgressValues from './utils'

const CircularProgress = ({
  currentStep,
  totalSteps,
  size = 'small',
  testID,
  animate = true,
}: Props) => {
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const previousProgress = useRef(0)
  const animationRef = useRef<number>()

  const {
    clampedStep,
    normalizedProgress,
    label,
    progressSize,
    arcLength,
    RADIUS,
    STROKE_WIDTH,
    FULL_CIRCLE,
  } = calculateProgressValues({ currentStep, totalSteps, size })

  const { container, center, text, icon } = circularProgress({
    size,
    webOptimized: true,
  })

  const strokeDashoffset = arcLength * 0.99 - animatedProgress * arcLength * 0.99

  const labelOpacity =
    animatedProgress < 0.85 ? 1 : Math.max(0, 1 - (animatedProgress - 0.85) / 0.15)
  const iconOpacity = animatedProgress >= 0.9 ? Math.min(1, (animatedProgress - 0.9) / 0.1) : 0
  const iconScale =
    animatedProgress >= 0.95 ? 0.8 + Math.min(1, (animatedProgress - 0.95) / 0.05) * 0.2 : 0.8

  useEffect(() => {
    if (!animate) {
      setAnimatedProgress(normalizedProgress)
      previousProgress.current = normalizedProgress
      return
    }

    const startProgress = previousProgress.current
    const targetProgress = normalizedProgress
    const startTime = performance.now()

    const animateProgress = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1)

      const easedProgress = 1 - Math.pow(1 - progress, 3)
      const currentAnimationProgress =
        startProgress + (targetProgress - startProgress) * easedProgress

      setAnimatedProgress(currentAnimationProgress)

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateProgress)
      } else {
        previousProgress.current = normalizedProgress
      }
    }

    animationRef.current = requestAnimationFrame(animateProgress)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [animate, normalizedProgress, clampedStep, totalSteps])

  const isCompleted = clampedStep === totalSteps

  return (
    <div
      className={container()}
      data-testid={testID}
      role="progressbar"
      aria-label={`${currentStep}/${totalSteps}`}
      aria-valuenow={clampedStep}
      aria-valuemin={0}
      aria-valuemax={totalSteps}
    >
      <svg
        width={progressSize}
        height={progressSize}
        style={{
          transform: `rotate(${ROTATION_DEGREES})`,
        }}
      >
        <circle
          stroke={COLOR.uiGreyMedium}
          fill="none"
          cx={progressSize / HALF_DIVISOR}
          cy={progressSize / HALF_DIVISOR}
          r={RADIUS}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${arcLength}, ${FULL_CIRCLE}`}
          strokeLinecap="round"
        />
        <circle
          stroke={COLOR.uiGreen}
          fill="none"
          cx={progressSize / HALF_DIVISOR}
          cy={progressSize / HALF_DIVISOR}
          r={RADIUS}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${arcLength}, ${FULL_CIRCLE}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: animate ? 'none' : 'stroke-dashoffset 0.3s ease',
          }}
        />
      </svg>

      <div className={center()}>
        <span
          className={text()}
          style={{
            opacity: labelOpacity,
          }}
        >
          {label}
        </span>

        {isCompleted && (
          <span
            data-testid="completion-icon"
            className={icon()}
            style={{
              opacity: iconOpacity,
              transform: `scale(${iconScale})`,
            }}
          >
            🎉
          </span>
        )}
      </div>
    </div>
  )
}

export default CircularProgress
